import { useEffect, useMemo, useRef, useState } from 'react'
import { cx } from './cx'
import type { Tone } from './Pill'
import s from './Calendar.module.css'

/**
 * Calendar — a shared month/agenda scheduling surface, data-driven and theme-aware.
 *
 * Patterns lifted from the established schedulers (Kendo React Scheduler · FullCalendar):
 * month day-grid with event chips + "+N more" overflow, an agenda/list view, a header
 * toolbar (prev · today · next + title + view switch), click-a-day-to-create,
 * click-an-event-to-open, drag-to-reschedule (month view), today highlight, out-of-month
 * dimming, and events coloured by a status field (chip tone). Week/Day time-grid views
 * (#16) add hour-slot scheduling with an all-day row + a current-time indicator.
 */
export interface CalendarEvent {
  id: string
  title: string
  start: string | Date
  end?: string | Date          // optional — Week/Day view sizes the block by duration
  allDay?: boolean             // pins to the all-day row in Week/Day view
  tone?: Tone
  meta?: string
}
export interface CalendarProps {
  events: CalendarEvent[]
  view?: CalendarView
  onViewChange?: (v: CalendarView) => void
  month?: Date
  onMonthChange?: (d: Date) => void
  onSelectDate?: (d: Date) => void
  onSelectEvent?: (e: CalendarEvent) => void
  /** When provided, month-view chips are draggable and dropping on a day fires this. */
  onMoveEvent?: (id: string, newStart: Date) => void
  weekStartsOn?: 0 | 1
  maxPerDay?: number
  className?: string
}
export type CalendarView = 'month' | 'week' | 'day' | 'agenda'

const D = (v: string | Date) => (v instanceof Date ? v : new Date(v))
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1)
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1)
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x }
const startOfWeek = (d: Date, wk: 0 | 1) => addDays(new Date(d.getFullYear(), d.getMonth(), d.getDate()), -((d.getDay() - wk + 7) % 7))
const isAllDay = (e: CalendarEvent) => e.allDay || (D(e.start).getHours() === 0 && D(e.start).getMinutes() === 0 && !e.end)
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
const MONTH_TITLE = (d: Date) => d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
const DOW = (weekStartsOn: 0 | 1) => {
  const base = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  return weekStartsOn === 1 ? [...base.slice(1), base[0]!] : base
}

export function Calendar({
  events, view: viewProp, onViewChange, month: monthProp, onMonthChange,
  onSelectDate, onSelectEvent, onMoveEvent, weekStartsOn = 1, maxPerDay = 3, className,
}: CalendarProps) {
  const today = new Date()
  const [viewState, setViewState] = useState<CalendarView>('month')
  const [monthState, setMonthState] = useState<Date>(startOfMonth(monthProp ?? today))
  const [anchor, setAnchor] = useState<Date>(new Date(today.getFullYear(), today.getMonth(), today.getDate()))  // Week/Day cursor
  const view = viewProp ?? viewState
  const month = monthProp ? startOfMonth(monthProp) : monthState
  const setView = (v: CalendarView) => { setViewState(v); onViewChange?.(v) }
  const setMonth = (d: Date) => { setMonthState(d); onMonthChange?.(d) }
  // Prev/next steps by the current view's unit; Today resets both cursors.
  const step = (dir: -1 | 1) => {
    if (view === 'week') setAnchor((a) => addDays(a, 7 * dir))
    else if (view === 'day') setAnchor((a) => addDays(a, dir))
    else setMonth(addMonths(month, dir))
  }
  const goToday = () => { setMonth(startOfMonth(today)); setAnchor(new Date(today.getFullYear(), today.getMonth(), today.getDate())) }
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor, weekStartsOn), i)), [anchor, weekStartsOn])

  // Bucket events by day for O(1) cell lookup.
  const byDay = useMemo(() => {
    const m = new Map<string, CalendarEvent[]>()
    for (const e of events) { const k = dayKey(D(e.start)); (m.get(k) ?? m.set(k, []).get(k)!).push(e) }
    for (const list of m.values()) list.sort((a, b) => +D(a.start) - +D(b.start))
    return m
  }, [events])

  // 6-week grid starting on the week that contains the 1st.
  const grid = useMemo(() => {
    const first = startOfMonth(month)
    const lead = (first.getDay() - weekStartsOn + 7) % 7
    const start = addDays(first, -lead)
    return Array.from({ length: 42 }, (_, i) => addDays(start, i))
  }, [month, weekStartsOn])

  // ── month-grid keyboard operability (UX-05 / #392) ─────────────────────────
  // The day cells were `div onClick` with no tabIndex or key handler: reading a
  // calendar worked (chips are buttons) but CREATING on a date did not. Roving
  // tabindex over the 42 cells: one tab stop enters the grid, arrows move by
  // day/week, Home/End jump the week, PageUp/PageDown step the month, Enter or
  // Space selects the date exactly as a click does.
  const [focusDay, setFocusDay] = useState<Date | null>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const pendingFocus = useRef<string | null>(null)
  // The cell that holds the grid's single tab stop: the focused day if visible,
  // else today when the grid shows it, else the 1st of the month.
  const inGrid = (d: Date | null) => !!d && grid.some((g) => sameDay(g, d))
  const rovingDay = inGrid(focusDay) ? focusDay! : (grid.some((g) => sameDay(g, today)) ? today : startOfMonth(month))
  // Arrowing past the grid edge changes month; the cell to focus only exists
  // AFTER that render, so the focus is finished in an effect.
  useEffect(() => {
    if (!pendingFocus.current) return
    gridRef.current?.querySelector<HTMLElement>(`[data-day="${pendingFocus.current}"]`)?.focus()
    pendingFocus.current = null
  })
  // Same-day month step for PageUp/PageDown, clamped to the target month's end
  // (Jan 31 + a month is Feb 28, not Mar 3). `addMonths` snaps to the 1st.
  const stepMonth = (d: Date, n: number) => {
    const first = new Date(d.getFullYear(), d.getMonth() + n, 1)
    const last = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
    return new Date(first.getFullYear(), first.getMonth(), Math.min(d.getDate(), last))
  }
  const moveFocus = (target: Date, forceMonth = false) => {
    setFocusDay(target)
    // Arrows only change month when they leave the grid; PageUp/PageDown ALWAYS
    // flip it — Jul 1 is visible as a trailing cell of June, but "next month"
    // must still show July.
    if (forceMonth || !grid.some((g) => sameDay(g, target))) setMonth(startOfMonth(target))
    pendingFocus.current = dayKey(target)
  }
  const onCellKey = (e: React.KeyboardEvent, day: Date) => {
    if (e.target !== e.currentTarget) return   // keys inside a chip belong to the chip
    switch (e.key) {
      case 'Enter': case ' ': e.preventDefault(); onSelectDate?.(day); break
      case 'ArrowRight': e.preventDefault(); moveFocus(addDays(day, 1)); break
      case 'ArrowLeft': e.preventDefault(); moveFocus(addDays(day, -1)); break
      case 'ArrowDown': e.preventDefault(); moveFocus(addDays(day, 7)); break
      case 'ArrowUp': e.preventDefault(); moveFocus(addDays(day, -7)); break
      case 'Home': e.preventDefault(); moveFocus(addDays(day, -((day.getDay() - weekStartsOn + 7) % 7))); break
      case 'End': e.preventDefault(); moveFocus(addDays(day, 6 - ((day.getDay() - weekStartsOn + 7) % 7))); break
      case 'PageDown': e.preventDefault(); moveFocus(stepMonth(day, 1), true); break
      case 'PageUp': e.preventDefault(); moveFocus(stepMonth(day, -1), true); break
    }
  }
  // Keyboard reschedule (the drag alternative): Shift+Arrows on a focused chip
  // move the event by a day (←/→) or a week (↑/↓).
  const onChipKey = (e: React.KeyboardEvent, ev: CalendarEvent, day: Date) => {
    if (!onMoveEvent || !e.shiftKey) return
    const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowDown' ? 7 : e.key === 'ArrowUp' ? -7 : 0
    if (!delta) return
    e.preventDefault(); e.stopPropagation()
    onMoveEvent(ev.id, addDays(day, delta))
  }
  const cellLabel = (day: Date, n: number) =>
    `${day.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}${n ? `, ${n} event${n === 1 ? '' : 's'}` : ''}`

  const title = view === 'month' ? MONTH_TITLE(month)
    : view === 'agenda' ? 'Agenda'
    : view === 'day' ? anchor.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : `Week of ${weekDays[0]!.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`

  const VIEW_LABEL: Record<CalendarView, string> = { month: 'Month', week: 'Week', day: 'Day', agenda: 'Agenda' }

  return (
    <div className={cx(s.cal, className)} data-testid="calendar">
      <header className={s.toolbar}>
        <div className={s.nav}>
          <button className={s.navBtn} aria-label="Previous" onClick={() => step(-1)}>‹</button>
          <button className={s.today} onClick={goToday}>Today</button>
          <button className={s.navBtn} aria-label="Next" onClick={() => step(1)}>›</button>
        </div>
        <div className={s.title} data-testid="cal-title">{title}</div>
        {/* The same false-tablist shape as SegmentedControl had (UX-12): tab
            roles with none of the tabs contract. It is a mutually-exclusive
            value → radio semantics, one tab stop, arrows move and select. */}
        <div className={s.views} role="radiogroup" aria-label="Calendar view">
          {(['month', 'week', 'day', 'agenda'] as CalendarView[]).map((v, i, all) => (
            <button key={v} type="button" role="radio" aria-checked={view === v}
              tabIndex={view === v ? 0 : -1}
              className={cx(s.viewBtn, view === v && s.viewOn)}
              data-testid={`cal-view-${v}`} onClick={() => setView(v)}
              onKeyDown={(e) => {
                const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0
                if (!d) return
                e.preventDefault()
                const next = all[(i + d + all.length) % all.length]!
                setView(next)
                ;(e.currentTarget.parentElement?.querySelector(`[data-testid="cal-view-${next}"]`) as HTMLElement)?.focus()
              }}>{VIEW_LABEL[v]}</button>
          ))}
        </div>
      </header>

      {view === 'month' ? (
        <div ref={gridRef} className={s.grid} role="grid" aria-label={MONTH_TITLE(month)}>
          {DOW(weekStartsOn).map((d) => <div key={d} className={s.dow} role="columnheader">{d}</div>)}
          {grid.map((day) => {
            const list = byDay.get(dayKey(day)) ?? []
            const out = day.getMonth() !== month.getMonth()
            const shown = list.slice(0, maxPerDay)
            const extra = list.length - shown.length
            return (
              <div key={dayKey(day)} role="gridcell" data-day={dayKey(day)}
                aria-label={cellLabel(day, list.length)}
                tabIndex={sameDay(day, rovingDay) ? 0 : -1}
                onFocus={() => setFocusDay(day)}
                onKeyDown={(e) => onCellKey(e, day)}
                className={cx(s.cell, out && s.out, sameDay(day, today) && s.todayCell)}
                onClick={() => onSelectDate?.(day)}
                onDragOver={onMoveEvent ? (e) => { e.preventDefault() } : undefined}
                onDrop={onMoveEvent ? (e) => { e.preventDefault(); const id = e.dataTransfer.getData('text/cal'); if (id) onMoveEvent(id, day) } : undefined}>
                <div className={s.dayNum}><span className={cx(sameDay(day, today) && s.todayNum)}>{day.getDate()}</span></div>
                <div className={s.chips}>
                  {shown.map((e) => (
                    <button key={e.id} className={cx(s.chip, s[`t_${e.tone ?? 'neutral'}`])}
                      title={onMoveEvent ? `${e.title} — Shift+arrows move it a day or a week` : e.title}
                      draggable={!!onMoveEvent}
                      onDragStart={onMoveEvent ? (ev) => ev.dataTransfer.setData('text/cal', e.id) : undefined}
                      data-testid={`cal-event-${e.id}`}
                      onKeyDown={(ev) => onChipKey(ev, e, day)}
                      onClick={(ev) => { ev.stopPropagation(); onSelectEvent?.(e) }}>
                      <span className={s.dot} /> <span className={s.chipTitle}>{e.title}</span>
                    </button>
                  ))}
                  {extra > 0 && (
                    // "+N more" was a <span> — the overflow was unreachable by
                    // keyboard AND by mouse. A real button that jumps to the
                    // day view, where every event on the day is shown.
                    <button type="button" className={s.more} data-testid={`cal-more-${dayKey(day)}`}
                      aria-label={`Show all ${list.length} events on ${day.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}`}
                      onClick={(ev) => { ev.stopPropagation(); setAnchor(new Date(day.getFullYear(), day.getMonth(), day.getDate())); setView('day') }}>
                      +{extra} more
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      ) : view === 'week' || view === 'day' ? (
        <TimeGridView days={view === 'day' ? [anchor] : weekDays} events={events} today={today}
          onSelectEvent={onSelectEvent} onSelectDate={onSelectDate} />
      ) : (
        <AgendaView events={events} month={month} onSelectEvent={onSelectEvent} />
      )}
    </div>
  )
}

const HOURS = Array.from({ length: 24 }, (_, h) => h)
const hourLabel = (h: number) => (h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`)
/* Where the time grid OPENS. It used to open at scrollTop 0 — midnight — so
   every Week/Day view began with seven empty hours and a clipped "12 AM"
   (design-sync kit finding, Calendar.module.css:97/101). It opens at the start
   of a working day instead, or earlier when an event on screen starts earlier:
   the grid must never open scrolled PAST something scheduled. */
const WORKDAY_START_HOUR = 7
/* An event shorter than an hour has room for ONE line (48px per hour). The
   stacked time-over-title layout clipped the title, which is the part a reader
   needs, so a short event lays its title first on a single row. */
const SHORT_EVENT_HOURS = 1

function TimeGridView({ days, events, today, onSelectEvent, onSelectDate }: {
  days: Date[]; events: CalendarEvent[]; today: Date
  onSelectEvent?: (e: CalendarEvent) => void; onSelectDate?: (d: Date) => void
}) {
  // Bucket timed + all-day events per day.
  const byDay = useMemo(() => {
    const m = new Map<string, { timed: CalendarEvent[]; allDay: CalendarEvent[] }>()
    for (const d of days) m.set(dayKey(d), { timed: [], allDay: [] })
    for (const e of events) {
      const b = m.get(dayKey(D(e.start))); if (!b) continue
      ;(isAllDay(e) ? b.allDay : b.timed).push(e)
    }
    return m
  }, [days, events])

  const nowTop = (today.getHours() + today.getMinutes() / 60) / 24 * 100
  const showNow = days.some((d) => sameDay(d, today))

  const scrollRef = useRef<HTMLDivElement>(null)
  const firstHour = useMemo(() => {
    let hour = WORKDAY_START_HOUR
    for (const bucket of byDay.values()) {
      for (const e of bucket.timed) hour = Math.min(hour, D(e.start).getHours())
    }
    return hour
  }, [byDay])
  // Once per mount (entering Week/Day), not on every event change: a reader
  // who has scrolled keeps their place when the data refreshes.
  const opened = useRef(false)
  useEffect(() => {
    const scroller = scrollRef.current
    if (opened.current || !scroller) return
    opened.current = true
    const row = scroller.querySelector<HTMLElement>(`[data-hour="${firstHour}"]`)
    const label = row?.firstElementChild as HTMLElement | null | undefined
    // The row's top plus its label's own (negative) offset, so the opening
    // hour's label is whole at the top edge rather than cut in half by it.
    if (row) scroller.scrollTop = Math.max(0, row.offsetTop + (label?.offsetTop ?? 0))
  }, [firstHour])

  return (
    <div className={cx(s.tg, days.length === 1 && s.tgDay)} data-testid="cal-timegrid">
      <div className={s.tgHead}>
        <div className={s.tgGutterHead} />
        {days.map((d) => (
          <button key={dayKey(d)} className={cx(s.tgDayHead, sameDay(d, today) && s.tgTodayHead)}
            onClick={() => onSelectDate?.(d)} data-testid={`cal-day-head-${dayKey(d)}`}>
            <span className={s.tgDow}>{d.toLocaleDateString(undefined, { weekday: 'short' })}</span>
            <span className={s.tgDayNum}>{d.getDate()}</span>
          </button>
        ))}
      </div>

      <div className={s.tgAllDay}>
        <div className={s.tgGutter}>all-day</div>
        {days.map((d) => (
          <div key={dayKey(d)} className={s.tgAllCell}>
            {(byDay.get(dayKey(d))?.allDay ?? []).map((e) => (
              <button key={e.id} className={cx(s.chip, s[`t_${e.tone ?? 'neutral'}`])} data-testid={`cal-event-${e.id}`}
                onClick={() => onSelectEvent?.(e)}><span className={s.dot} /> <span className={s.chipTitle}>{e.title}</span></button>
            ))}
          </div>
        ))}
      </div>

      <div ref={scrollRef} className={s.tgScroll} data-testid="cal-timegrid-scroll">
        <div className={s.tgBody} style={{ ['--cols' as string]: days.length }}>
          <div className={s.tgGutterCol}>
            {HOURS.map((h) => <div key={h} className={s.tgHour} data-hour={h}><span>{hourLabel(h)}</span></div>)}
          </div>
          {days.map((d) => (
            <div key={dayKey(d)} className={cx(s.tgCol, sameDay(d, today) && s.tgTodayCol)}
              onClick={() => onSelectDate?.(d)}>
              {HOURS.map((h) => <div key={h} className={s.tgSlot} />)}
              {(byDay.get(dayKey(d))?.timed ?? []).map((e) => {
                const st = D(e.start); const top = (st.getHours() + st.getMinutes() / 60) / 24 * 100
                const dur = e.end ? Math.max(0.5, (+D(e.end) - +st) / 3.6e6) : 1
                const height = Math.min(24 - (st.getHours() + st.getMinutes() / 60), dur) / 24 * 100
                const short = dur < SHORT_EVENT_HOURS
                return (
                  <button key={e.id} className={cx(s.tgEvent, short && s.tgShort, s[`t_${e.tone ?? 'neutral'}`])}
                    data-testid={`cal-event-${e.id}`} data-tone={e.tone ?? 'neutral'} data-short={short || undefined}
                    title={e.title}
                    style={{ top: `${top}%`, height: `${height}%` }}
                    onClick={(ev) => { ev.stopPropagation(); onSelectEvent?.(e) }}>
                    {short && <span className={s.tgEvTitle}>{e.title}</span>}
                    <span className={s.tgEvTime}>{st.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
                    {!short && <span className={s.tgEvTitle}>{e.title}</span>}
                  </button>
                )
              })}
              {showNow && sameDay(d, today) && <div className={s.tgNow} style={{ top: `${nowTop}%` }} data-testid="cal-now" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function AgendaView({ events, month, onSelectEvent }: { events: CalendarEvent[]; month: Date; onSelectEvent?: (e: CalendarEvent) => void }) {
  const groups = useMemo(() => {
    const inMonth = events
      .map((e) => ({ e, d: D(e.start) }))
      .filter(({ d }) => d.getMonth() === month.getMonth() && d.getFullYear() === month.getFullYear())
      .sort((a, b) => +a.d - +b.d)
    const m = new Map<string, { day: Date; items: CalendarEvent[] }>()
    for (const { e, d } of inMonth) {
      const k = dayKey(d)
      if (!m.has(k)) m.set(k, { day: d, items: [] })
      m.get(k)!.items.push(e)
    }
    return [...m.values()]
  }, [events, month])

  if (groups.length === 0) return <div className={s.empty}>Nothing scheduled in {MONTH_TITLE(month)}.</div>
  return (
    <div className={s.agenda} data-testid="cal-agenda">
      {groups.map(({ day, items }) => (
        <div key={dayKey(day)} className={s.agRow}>
          <div className={s.agDate}>
            <div className={s.agDow}>{day.toLocaleDateString(undefined, { weekday: 'short' })}</div>
            <div className={s.agNum}>{day.getDate()}</div>
          </div>
          <div className={s.agItems}>
            {items.map((e) => (
              <button key={e.id} className={s.agItem} data-testid={`cal-agenda-${e.id}`} onClick={() => onSelectEvent?.(e)}>
                <span className={cx(s.dot, s[`t_${e.tone ?? 'neutral'}`])} />
                <span className={s.agTitle}>{e.title}</span>
                {e.meta && <span className={s.agMeta}>{e.meta}</span>}
                {/* An all-day event has no time; "00:00" read as a midnight
                    appointment (design-sync kit finding, Calendar.tsx:353). */}
                <span className={s.agTime}>{isAllDay(e) ? 'All day' : D(e.start).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
